import { FC } from 'react'

import { Box, Typography, alpha } from '@mui/material'

interface DiagnosisGapChipProps {
  /** Uppercase pill label, e.g. "Biggest gap" / "Gap" / "In sync". */
  label: string
  /**
   * Numeric readout trailing the label. Omit it for labels that don't quantify
   * anything (an "In sync" pill shouldn't claim a gap of ±0).
   */
  value?: string
  accent: string
  /**
   * Promotes the pill to the filled variant. Used for the single top-priority
   * card so one cue stands out from the outlined crowd.
   */
  filled: boolean
}

/**
 * Small pill quantifying a gap on a diagnosis card. Outlined in the category
 * accent by default; the headline card gets the filled treatment.
 *
 * Shared by the single-game and comparison diagnosis cards — the two differ
 * only in how they word the label and derive the value, not in the geometry.
 */
export const DiagnosisGapChip: FC<DiagnosisGapChipProps> = ({ label, value, accent, filled }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 0.875,
      py: 0.375,
      borderRadius: 999,
      flexShrink: 0,
      ...(filled
        ? { backgroundColor: accent, color: 'common.white', border: '1px solid', borderColor: accent }
        : { backgroundColor: 'transparent', color: accent, border: '1px solid', borderColor: alpha(accent, 0.5) }),
    }}
  >
    <Typography
      component="span"
      sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'inherit', lineHeight: 1 }}
    >
      {label}
    </Typography>
    {value !== undefined && (
      <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'inherit', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
        {value}
      </Typography>
    )}
  </Box>
)
